import React from "react";
import { logRuntimeDiagnostic } from "../services/diagRuntime";
import { isRecoverableChunkLoadError, rechargerEnRenouvelantLeDocument, reloadPageOnce } from "../services/browser/reloadGuard";

interface RuntimeErrorBoundaryProps {
    children: React.ReactNode;
}

interface RuntimeErrorBoundaryState {
    hasError: boolean;
    isChunkLoadError: boolean;
    reloadScheduled: boolean;
    /** Un code court a citer au support, calcule sur le nom et le message. */
    codeIncident: string | null;
}

/**
 * Deux personnes qui rencontrent la meme erreur citent le meme code.
 *
 * Il est joint au diagnostic et affiche a l'ecran : sans lui, « j'ai eu une
 * erreur » ne mene nulle part, et personne ne va lire un journal depuis un
 * telephone.
 */
function calculerCodeIncident(error: Error): string {
    const source = `${error.name}|${error.message}`;
    let empreinte = 0;
    for (let i = 0; i < source.length; i++) {
        empreinte = ((empreinte << 5) - empreinte) + source.charCodeAt(i);
        empreinte = empreinte & empreinte;
    }
    return Math.abs(empreinte).toString(36).toUpperCase().slice(0, 6);
}

class RuntimeErrorBoundary extends React.Component<RuntimeErrorBoundaryProps, RuntimeErrorBoundaryState> {
    state: RuntimeErrorBoundaryState = {
        hasError: false,
        isChunkLoadError: false,
        reloadScheduled: false,
        codeIncident: null,
    };

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo) {
        const isChunkLoadError = isRecoverableChunkLoadError(error);
        const reloadScheduled = isChunkLoadError ? reloadPageOnce("chunk-load-error") : false;
        const codeIncident = calculerCodeIncident(error);

        // L'etat etait pose UNIQUEMENT pour une erreur de chunk : une erreur
        // ordinaire laissait donc `isChunkLoadError` a faux par defaut plutot
        // que par constat, et le code d'incident ne serait jamais arrive a
        // l'ecran.
        this.setState({ isChunkLoadError, reloadScheduled, codeIncident });

        void logRuntimeDiagnostic("react-boundary", `${error.name}:${error.message}`, {
            stack: error.stack,
            componentStack: info.componentStack,
            isChunkLoadError,
            reloadScheduled,
            codeIncident,
        });
    }

    render() {
        if (this.state.hasError) {
            return (
                <main style={styles.page}>
                    <section style={styles.panel}>
                        <h1 style={styles.title}>
                            {this.state.isChunkLoadError ? "Mise a jour de l'application" : "Erreur d'affichage"}
                        </h1>
                        <p style={styles.text}>
                            {this.state.reloadScheduled
                                ? "La page va se recharger automatiquement."
                                : this.state.isChunkLoadError
                                    // Le rechargement automatique a deja eu lieu et n'a pas
                                    // suffi : le dire, plutot que de reproposer le meme geste
                                    // comme s'il n'avait pas ete tente.
                                    ? "Le rechargement automatique n'a pas suffi. Réessayez : cette fois l'application ira rechercher sa dernière version."
                                    // Un plantage de l'ecran, pas une mise a jour. Recharger
                                    // rejoue le meme bug : on ne le promet donc pas. Le code
                                    // permet de relier ce que la personne voit a ce que le
                                    // journal a enregistre.
                                    : "Cet écran n'a pas pu s'afficher. Le rechargement donnera probablement le même résultat : signalez ce code au support."}
                        </p>
                        {!this.state.isChunkLoadError && this.state.codeIncident &&
                            <p style={styles.code}>code {this.state.codeIncident}</p>
                        }
                        {/* Ce bouton faisait `location.reload()`, c'est-a-dire
                            exactement ce qui venait d'echouer : le navigateur
                            peut reservir le meme index.html depuis son cache, et
                            c'est lui le probleme. Il renouvelle desormais le
                            document avant de recharger. */}
                        <button
                            type="button"
                            style={styles.button}
                            onClick={() => { void rechargerEnRenouvelantLeDocument(); }}
                        >
                            Recharger
                        </button>
                    </section>
                </main>
            );
        }

        return this.props.children;
    }
}

const styles: Record<string, React.CSSProperties> = {
    page: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        background: "#f7f8fb",
        color: "#1f2933",
        fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    },
    panel: {
        width: "100%",
        maxWidth: 420,
        padding: 24,
        borderRadius: 8,
        background: "#ffffff",
        boxShadow: "0 8px 24px rgba(15, 23, 42, 0.12)",
        textAlign: "center",
    },
    title: {
        margin: "0 0 8px",
        fontSize: 22,
        lineHeight: 1.25,
        fontWeight: 700,
    },
    code: {
        margin: "8px 0 0",
        fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
        fontSize: 18,
        fontWeight: 700,
        letterSpacing: 1,
    },
    text: {
        margin: "0 0 20px",
        fontSize: 15,
        lineHeight: 1.5,
        color: "#52616f",
    },
    button: {
        minHeight: 42,
        padding: "0 18px",
        border: 0,
        borderRadius: 6,
        background: "#1677ff",
        color: "#ffffff",
        fontSize: 15,
        fontWeight: 600,
        cursor: "pointer",
    },
};

export default RuntimeErrorBoundary;
